import PageHeader from "../components/layout/PageHeader";
import ContactCTA from "../components/sections/ContactCTA";
import { featuredProjects } from "../lib/content";
import { motion } from "framer-motion";
import { MapPin } from "lucide-react";

const additional = [
    {
        title: "University Research Complex",
        sector: "Education",
        location: "Houston, TX",
        scope: "250,000 SF · Rice University · Dedicated Sept 2023",
        year: "2023",
        image: "https://images.unsplash.com/photo-1562774053-701939374585?w=1600",
    },
    {
        title: "Pharmaceutical Plant",
        sector: "Advanced Manufacturing",
        location: "Texas, USA",
        scope: "236-acre · Oral Small Molecule GLP-1 Receptor",
        year: "2024",
        image: "https://customer-assets.emergentagent.com/job_pes-modern/artifacts/d2h53jte_IMG_0017.webp",
    },
    {
        title: "Urban Mixed-Use Tower",
        sector: "Highrise Residential",
        location: "Downtown Austin, TX",
        scope: "60 Stories · 530,731 SF · Mixed-Use",
        year: "2022",
        image: "https://images.pexels.com/photos/32831921/pexels-photo-32831921.jpeg",
    },
    {
        title: "Corporate Campus",
        sector: "Commercial",
        location: "Seattle, WA",
        scope: "3-Building Innovation Campus · LEED Platinum",
        year: "2022",
        image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600",
    },
    {
        title: "K-12 Science Academy",
        sector: "Education",
        location: "Texas, USA",
        scope: "432,685 SF · Academic + CTE + Athletic Wings",
        year: "2022",
        image: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1600",
    },
    {
        title: "Distribution Warehouse / Fulfillment Center",
        sector: "Commercial",
        location: "Texas, USA",
        scope: "403,800 SF · Fulfillment Operations",
        year: "2021",
        image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1600",
    },
    {
        title: "Luxury Retail Plaza",
        sector: "Commercial",
        location: "Houston, TX",
        scope: "252,000 SF · Open-Air Luxury Shopping",
        year: "2015",
        image: "https://customer-assets.emergentagent.com/job_pes-modern/artifacts/erdl1ag9_IMG_0013.jpeg",
    },
    {
        title: "Cleanroom Design",
        sector: "Advanced Manufacturing",
        location: "Research Triangle, NC",
        scope: "ISO-7 Cleanrooms · cGMP Compliant Biomedical",
        year: "2023",
        image: "https://customer-assets.emergentagent.com/job_pes-modern/artifacts/fkw5y69l_IMG_0018.jpeg",
    },
    {
        title: "Luxury High-End Palaces & Mansions",
        sector: "Residential",
        location: "Multi-Region, USA",
        scope: "Estate Residences · Custom MEP Systems",
        year: "2023",
        image: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1600&q=80",
    },
    {
        title: "Single Family & ADUs Portfolio",
        sector: "Residential",
        location: "50+ Cities, California",
        scope: "Custom Homes & Accessory Dwelling Units",
        year: "2024",
        image: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1600&q=80",
    },
    {
        title: "Hotel Saint Augustine — Bunkhouse / Hyatt",
        sector: "Commercial",
        location: "Montrose, Houston, TX",
        scope: "71-Room Boutique Hotel · 2.2 Acres · Opened Jan 2024",
        year: "2024",
        image: "https://login.bunkhousehotels.com/hotel-saint-augustine/wp-content/uploads/sites/15/2024/12/HSA0HomePage-3.webp",
    },
];

const all = [...featuredProjects, ...additional];

export default function Projects() {
    return (
        <div data-testid="projects-page">
            <PageHeader
                eyebrow="Selected Work"
                title={<>A decade of projects that <span className="text-blue-400">move industries</span> forward.</>}
                description="From semiconductor fabs to 650-bed hospitals — a selection of the systems we've engineered over the last ten years."
            />

            <section className="py-16 md:py-20 border-t border-white/5">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {all.map((p, i) => (
                            <motion.article
                                key={`${p.title}-${i}`}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                                className="group relative overflow-hidden rounded-2xl border border-white/5 hover:border-blue-500/40 transition-all duration-500 aspect-[4/5]"
                                data-testid={`project-grid-${i}`}
                            >
                                <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />
                                <div className="relative h-full p-6 flex flex-col justify-end">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/20 border border-blue-500/30 backdrop-blur-md text-[10px] font-mono tracking-[0.2em] uppercase text-cyan-300">
                                            {p.sector}
                                        </span>
                                        <span className="text-xs font-mono text-slate-400">{p.year}</span>
                                    </div>
                                    <h3 className="font-heading font-black text-white text-xl md:text-2xl leading-tight tracking-tight mb-3">
                                        {p.title}
                                    </h3>
                                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                                        <MapPin className="w-3 h-3 text-cyan-400" /> {p.location}
                                    </div>
                                    <div className="text-xs font-mono text-slate-500">{p.scope}</div>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                </div>
            </section>

            <ContactCTA compact />
        </div>
    );
}
